// Resident-only screens: the request intake (triage → answer → ticket+email),
// the resident's ticket list, and the manager-contacts editor used in board
// Settings. Chat, laws browsing and appearance are shared with the board view.

import React, { useState } from "react";
import { AlertCircle, ArrowRight, Check, ChevronRight, Mail, Ticket } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge, Button, Console, Display, Field, Kicker, Rule, inputStyle } from "./ui.jsx";
import { CATEGORIES, MANAGER_ROLES, categoryLabel, managerLabel } from "./triage.js";
import { LayoutDashboard, MessageSquare, BookOpen, Settings as SettingsIcon } from "lucide-react";

const Markdown = ({ children }) => (
  <div style={{ font: "400 15px/1.6 var(--bq-font-body)", color: "var(--ink)", textWrap: "pretty" }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ""}</ReactMarkdown>
  </div>
);

export const RESIDENT_TABS = [
  { id: "dashboard", num: "01", label: "Dashboard" },
  { id: "request", num: "02", label: "Request" },
  { id: "residentiq", num: "03", label: "ResidentIQ" },
  { id: "assoclaws", num: "04", label: "Laws" },
  { id: "settings", num: "05", label: "Settings" },
];

export const RESIDENT_TAB_ICONS = {
  dashboard: LayoutDashboard,
  request: Ticket,
  residentiq: MessageSquare,
  assoclaws: BookOpen,
  settings: SettingsIcon,
};

export const RESIDENT_SUGGESTIONS = [
  "Can I repaint my front door a different color?",
  "The sprinklers by the mailboxes have been running all night",
  "What are the quiet hours here?",
  "I need a copy of last month's board minutes",
  "My neighbour's tree branches are over my patio",
];

/** Board/Resident switch — lives in the mobile menu and the desktop sidebar. */
export function RoleSwitch({ role, onRole, style, disableBoard = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, ...style }}>
      <Kicker tone="muted">Viewing as</Kicker>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[["board", "Board view", "Resolutions, drafting, setup"], ["resident", "Resident view", "Ask, request, track"]].map(([id, label, blurb]) => {
          const on = role === id;
          const disabled = id === "board" && disableBoard;
          return (
            <button key={id}
              onClick={() => !disabled && onRole(id)}
              disabled={disabled}
              title={disabled ? "Board access is not enabled for your account" : undefined}
              style={{
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 5,
                textAlign: "left",
                cursor: disabled ? "not-allowed" : on ? "default" : "pointer",
                padding: "10px 11px", minHeight: 62,
                border: on ? "1px solid var(--accent)" : "1px solid var(--rule2)",
                background: on ? "var(--accentTint)" : "transparent",
                opacity: disabled ? 0.4 : 1,
              }}>
              <span style={{ font: "700 11px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", color: on ? "var(--ink)" : "var(--muted2)" }}>{label}</span>
              <span style={{ font: "400 10.5px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>{blurb}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact Board/Resident toggle -- sits inline next to the "BoardIQ"
 * wordmark in the desktop sidebar header. Same role/onRole/disableBoard
 * contract as RoleSwitch above (which stays around for the mobile menu's
 * fuller "Viewing as" card, where there's no adjacent wordmark to anchor
 * a compact switch to).
 */
export function RoleToggle({ role, onRole, disableBoard = false, style }) {
  const isResident = role === "resident";
  // The only illegal move is resident -> board when board access isn't
  // enabled for this account; board -> resident is always allowed.
  const locked = isResident && disableBoard;

  const flip = () => { if (!locked) onRole(isResident ? "board" : "resident"); };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 9, flex: "none", ...style }}>
      <span style={{
        font: "700 9.5px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase",
        color: isResident ? "var(--muted2)" : "var(--ink)", transition: "color 0.15s ease",
      }}>
        Board
      </span>

      {/* iOS-style switch: a pill track that recolors on, with a circular
          knob that slides between its two ends -- role is the on/off
          state (resident = on), Board/Resident labels flank it since a
          bare on/off switch alone wouldn't say which side means what. */}
      <button
        type="button" role="switch" aria-checked={isResident}
        onClick={flip} disabled={locked}
        title={locked ? "Board access is not enabled for your account" : "Switch to " + (isResident ? "Board" : "Resident") + " view"}
        style={{
          position: "relative", flex: "none", width: 42, height: 24,
          border: "none", borderRadius: 999, padding: 0,
          background: isResident ? "var(--accent)" : "var(--rule2)",
          cursor: locked ? "not-allowed" : "pointer",
          opacity: locked ? 0.45 : 1,
          transition: "background-color 0.18s ease",
        }}>
        <span style={{
          position: "absolute", top: 2, left: isResident ? 20 : 2,
          width: 20, height: 20, borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.25), 0 1px 4px rgba(0,0,0,0.18)",
          transition: "left 0.18s ease",
        }} />
      </button>

      <span style={{
        font: "700 9.5px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase",
        color: isResident ? "var(--ink)" : "var(--muted2)", transition: "color 0.15s ease",
      }}>
        Resident
      </span>
    </div>
  );
}

const ConfidenceBar = ({ value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <div style={{ flex: 1, height: 4, background: "var(--rule)" }}>
      <div style={{ height: 4, background: value >= 0.6 ? "var(--accent)" : "var(--rule2)", width: Math.round(value * 100) + "%" }} />
    </div>
    <span style={{ flex: "none", font: "400 10px/1 var(--bq-font-mono)", color: "var(--muted)" }}>{Math.round(value * 100)}%</span>
  </div>
);

/**
 * The resident's front door: describe what you want in plain words, ResidentIQ
 * decides what kind of request it is, answers it, and opens a routed ticket.
 */
export function ResidentIntake({ riq, managers, debugOn, onBrowseTickets }) {
  const [text, setText] = useState("");
  const { intake } = riq;
  const busy = intake.stage === "triaging" || intake.stage === "submitting";

  const submitText = () => {
    const t = text.trim();
    if (!t || busy) return;
    riq.describe(t);
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "14px 20px 20px", display: "flex", flexDirection: "column", gap: 18 }}>
      {/* ── intake ── */}
      {intake.stage === "idle" && (
        <>
          <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14 }}>
            <Display size={20} style={{ marginBottom: 6 }}>What do you need?</Display>
            <p style={{ margin: 0, font: "400 14px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
              Describe it in your own words. ResidentIQ works out whether it's a question, an
              architectural approval, maintenance or a records request — answers what the documents
              say, then opens a ticket with the right manager.
            </p>
          </div>

          <textarea
            value={text}
            rows={5}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. I'd like to replace my patio fence with a taller one, and the gate hinge is broken"
            style={{ ...inputStyle, minHeight: 132, resize: "vertical", font: "400 13px/1.5 var(--bq-font-mono)" }}
          />
          <Button onClick={submitText} disabled={!text.trim()}>Submit request</Button>

          <div>
            <Kicker tone="muted" style={{ marginBottom: 8 }}>Residents often start with</Kicker>
            <div style={{ borderTop: "1px solid var(--rule)" }}>
              {RESIDENT_SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => { setText(s); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                    width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid var(--rule)",
                    background: "transparent", color: "var(--ink)", padding: "13px 2px", minHeight: 48,
                    font: "500 14px/1.3 var(--bq-font-body)", cursor: "pointer",
                  }}>
                  <span>{s}</span>
                  <ArrowRight size={16} color="var(--accent)" strokeWidth={2.5} style={{ flex: "none" }} />
                </button>
              ))}
            </div>
          </div>

          {riq.tickets.length > 0 && (
            <Button variant="secondary" onClick={onBrowseTickets} style={{ justifyContent: "space-between" }}>
              <span>My requests ({riq.tickets.length})</span>
              <ChevronRight size={14} strokeWidth={2.5} />
            </Button>
          )}
        </>
      )}

      {intake.stage === "triaging" && (
        <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 14, height: 14, background: "var(--accent)", animation: "bq-pulse 1s ease-in-out infinite" }} />
            <span style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase" }}>Reading your request</span>
          </div>
          <div style={{ height: 2, background: "var(--rule)", overflow: "hidden" }}>
            <div style={{ height: 2, background: "var(--accent)", transformOrigin: "left", animation: "bq-bar 1.8s linear forwards" }} />
          </div>
          <span style={{ font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--muted)" }}>
            Classifying · searching the CC&Rs and Davis-Stirling
          </span>
        </div>
      )}

      {/* ── review: what it is, what the documents say, where it goes ── */}
      {(intake.stage === "review" || intake.stage === "submitting") && intake.triage && (
        <>
          <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <Kicker>Identified as</Kicker>
            <Display size={22}>{categoryLabel(intake.triage.category)}</Display>
            <ConfidenceBar value={intake.triage.confidence} />
            {intake.triage.reasons && intake.triage.reasons.length > 0 && (
              <span style={{ font: "400 11.5px/1.45 var(--bq-font-body)", color: "var(--muted)" }}>
                {intake.triage.reasons.join(" · ")}
              </span>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {CATEGORIES.map((c) => {
                const on = c.id === intake.triage.category;
                return (
                  <button key={c.id} onClick={() => riq.overrideCategory(c.id)}
                    style={{
                      border: on ? "1px solid var(--ink)" : "1px solid var(--rule2)",
                      background: on ? "var(--ink)" : "transparent",
                      color: on ? "var(--bg)" : "var(--muted2)",
                      padding: "6px 9px", minHeight: 32, cursor: on ? "default" : "pointer",
                      font: "600 9.5px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase",
                    }}>{c.label}</button>
                );
              })}
            </div>
          </div>

          {intake.answer && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Kicker tone="muted">What the documents say</Kicker>
              <Markdown>{intake.answer}</Markdown>
              {intake.sources.length > 0 && (
                <div style={{ background: "var(--surface)", borderLeft: "2px solid var(--accent)" }}>
                  {intake.sources.slice(0, 3).map((s) => (
                    <div key={s.id} style={{ padding: "11px 13px", borderBottom: "1px solid var(--rule)" }}>
                      <div style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--accentDeep)", marginBottom: 5 }}>
                        {s.label}{s.document ? " · " + s.document : ""}
                      </div>
                      <div style={{ font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--body)" }}>{s.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Kicker tone="muted">Routing to</Kicker>
            <div style={{ border: "1px solid var(--ink)", background: "var(--surface)", padding: "12px 13px", display: "flex", alignItems: "center", gap: 10 }}>
              <Mail size={16} color="var(--accent)" strokeWidth={2} style={{ flex: "none" }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ font: "700 13px/1.2 var(--bq-font-display)" }}>{managerLabel(intake.triage.area)}</div>
                <div style={{ font: "400 11.5px/1.3 var(--bq-font-mono)", color: managers[intake.triage.area] ? "var(--muted2)" : "var(--accentDeep)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {managers[intake.triage.area] || "No address on file — set it in Association settings"}
                </div>
              </div>
              {!intake.routedBySystem && <Badge tone="muted">Changed</Badge>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {MANAGER_ROLES.map((m) => {
                const on = m.id === intake.triage.area;
                return (
                  <button key={m.id} onClick={() => riq.overrideArea(m.id)}
                    style={{
                      border: on ? "1px solid var(--accent)" : "1px solid var(--rule2)",
                      background: on ? "var(--accentTint)" : "transparent",
                      color: on ? "var(--ink)" : "var(--muted2)",
                      padding: "6px 9px", minHeight: 32, cursor: on ? "default" : "pointer",
                      font: "600 9.5px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase",
                    }}>{m.label}</button>
                );
              })}
            </div>
          </div>

          <p style={{ margin: 0, font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--muted)" }}>
            Confirm and this becomes a tracked ticket -- no email is sent from here.
          </p>

          {intake.error && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 9, border: "1px solid var(--accent)", padding: "11px 12px" }}>
              <AlertCircle size={15} color="var(--accent)" strokeWidth={2} style={{ flex: "none", marginTop: 1 }} />
              <span style={{ font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--accentDeep)" }}>{intake.error}</span>
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={riq.submit} disabled={busy}>
              {intake.stage === "submitting" ? "Submitting…" : "Submit as a ticket"}
            </Button>
            <Button variant="secondary" onClick={riq.resetIntake} disabled={busy}
              style={{ padding: "9px 12px", minHeight: 44, font: "600 10px/1 var(--bq-font-display)" }}>
              Start over
            </Button>
          </div>
        </>
      )}

      {/* ── submitted ── */}
      {intake.stage === "submitted" && intake.ticket && (
        <>
          <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <Kicker>Ticket opened</Kicker>
            <Display size={26}>{intake.ticket.number || intake.ticket.id}</Display>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              <Badge tone="solid"><Check size={10} strokeWidth={3.5} />{intake.ticket.status || "Open"}</Badge>
              <Badge tone="outline">{categoryLabel(intake.triage.category)}</Badge>
            </div>
            <p style={{ margin: 0, font: "400 13.5px/1.55 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
              Saved and routed to {managerLabel(intake.triage.area)}. Track it any time under My Requests.
            </p>
          </div>

          {intake.answer && (
            <div style={{ background: "var(--surface)", borderLeft: "2px solid var(--ink)", padding: "12px 13px" }}>
              <Kicker tone="muted" style={{ marginBottom: 6 }}>Your answer, for reference</Kicker>
              <Markdown>{intake.answer}</Markdown>
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={riq.resetIntake}>New request</Button>
            <Button variant="secondary" onClick={onBrowseTickets} style={{ padding: "9px 12px", minHeight: 44, font: "600 10px/1 var(--bq-font-display)" }}>
              My requests
            </Button>
          </div>
        </>
      )}

      {debugOn && riq.debugLines.length > 0 && (
        <Console title="ResidentIQ debug console" endpoint={"POST /resident/triage"} lines={riq.debugLines} open onToggle={() => {}} maxHeight={150} />
      )}
    </div>
  );
}

export function TicketList({ tickets, onBack, onOpenNew, onOpenTicket }) {
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 20px 12px" }}>
        <Button variant="ghost" onClick={onBack} style={{ gap: 6 }}>
          <ChevronRight size={14} strokeWidth={2.5} style={{ transform: "rotate(180deg)" }} />New request
        </Button>
        <Kicker tone="muted">{tickets.length} on record</Kicker>
      </div>
      <div style={{ borderTop: "1px solid var(--ink)", margin: "0 20px" }}>
        {tickets.map((t) => (
          <button key={t.id || t.number} onClick={() => onOpenTicket && onOpenTicket(t.id)}
            style={{
              display: "flex", flexDirection: "column", gap: 6, padding: "14px 2px", borderBottom: "1px solid var(--rule)",
              width: "100%", textAlign: "left", border: "none", borderBottomWidth: 1, borderBottomStyle: "solid", borderBottomColor: "var(--rule)",
              background: "transparent", cursor: onOpenTicket ? "pointer" : "default", font: "inherit",
            }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%" }}>
              <span style={{ font: "600 11px/1 var(--bq-font-display)", letterSpacing: ".08em", color: "var(--muted2)" }}>{t.number || t.id}</span>
              {(t.status || "open").toLowerCase() === "closed"
                ? <Badge tone="solid"><Check size={10} strokeWidth={3.5} />Closed</Badge>
                : <Badge tone="outline">{t.status || "Open"}</Badge>}
            </div>
            <span style={{ font: "600 14.5px/1.3 var(--bq-font-display)", color: "var(--ink)" }}>{t.subject || t.description}</span>
            <span style={{ font: "400 11.5px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>
              {categoryLabel(t.category)} · {managerLabel(t.area)}
            </span>
          </button>
        ))}
      </div>
      {tickets.length === 0 && (
        <p style={{ margin: 20, font: "400 13.5px/1.5 var(--bq-font-body)", color: "var(--muted)" }}>
          Nothing submitted yet. <button onClick={onOpenNew} style={{ border: "none", background: "transparent", color: "var(--accentDeep)", cursor: "pointer", font: "inherit", padding: 0 }}>Describe a request</button> to open your first ticket.
        </p>
      )}
      <div style={{ height: 16 }} />
    </div>
  );
}

/**
 * Ticket detail -- the "click a request" pull-up. Shows the full request,
 * ResidentIQ's answer, and the comment thread (already latest-first from
 * the backend). The description can be edited in place, but only by the
 * ticket's own originator -- checked client-side for UX (hiding the
 * button) and enforced again server-side (ticketing_client.py 403s
 * anyone else), so hiding it here is a nicety, not the security boundary.
 */
export function TicketDetail({ riq, onBack }) {
  const { ticketDetail: t, ticketDetailStatus, ticketDetailError } = riq;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [commentText, setCommentText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isOriginator = !!(t && t.requester &&
    riq.requesterIdentifier(riq.resident).toLowerCase() === String(t.requester.identifier || "").toLowerCase());

  const startEdit = () => { setDraft(t.description || ""); setEditing(true); setError(""); };

  const saveEdit = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await riq.editTicket(draft.trim());
      setEditing(false);
    } catch (err) {
      setError(err?.message || "Couldn't save your changes.");
    } finally {
      setBusy(false);
    }
  };

  const submitComment = async () => {
    if (!commentText.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await riq.addTicketComment(commentText.trim());
      setCommentText("");
    } catch (err) {
      setError(err?.message || "Couldn't add your comment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "14px 20px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
      <Button variant="ghost" onClick={onBack} style={{ gap: 6, alignSelf: "flex-start" }}>
        <ChevronRight size={14} strokeWidth={2.5} style={{ transform: "rotate(180deg)" }} />Back to requests
      </Button>

      {ticketDetailStatus === "loading" && (
        <span style={{ font: "400 13px/1.4 var(--bq-font-body)", color: "var(--muted)" }}>Loading…</span>
      )}

      {ticketDetailStatus === "error" && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9, border: "1px solid var(--accent)", padding: "11px 12px" }}>
          <AlertCircle size={15} color="var(--accent)" strokeWidth={2} style={{ flex: "none", marginTop: 1 }} />
          <span style={{ font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--accentDeep)" }}>{ticketDetailError}</span>
        </div>
      )}

      {ticketDetailStatus === "ready" && t && (
        <>
          <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <Kicker tone="muted">{t.number || t.id}</Kicker>
              {(t.status || "open").toLowerCase() === "closed"
                ? <Badge tone="solid"><Check size={10} strokeWidth={3.5} />Closed</Badge>
                : <Badge tone="outline">{t.status || "Open"}</Badge>}
            </div>
            <Display size={22}>{t.subject || t.description}</Display>
            <span style={{ font: "400 11.5px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>
              {categoryLabel(t.category)} · {managerLabel(t.area)}
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Kicker tone="muted">Request</Kicker>
              {isOriginator && !editing && (
                <button onClick={startEdit} style={{ border: "none", background: "transparent", color: "var(--accentDeep)", cursor: "pointer", font: "600 10.5px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", padding: 0 }}>
                  Edit
                </button>
              )}
            </div>
            {editing ? (
              <>
                <textarea
                  value={draft}
                  rows={5}
                  onChange={(e) => setDraft(e.target.value)}
                  style={{ ...inputStyle, minHeight: 120, resize: "vertical", font: "400 13px/1.5 var(--bq-font-mono)" }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button onClick={saveEdit} disabled={busy || !draft.trim()}>{busy ? "Saving…" : "Save"}</Button>
                  <Button variant="secondary" onClick={() => setEditing(false)} disabled={busy}
                    style={{ padding: "9px 12px", minHeight: 44, font: "600 10px/1 var(--bq-font-display)" }}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <p style={{ margin: 0, font: "400 14px/1.55 var(--bq-font-body)", color: "var(--ink)", textWrap: "pretty", whiteSpace: "pre-wrap" }}>
                {t.description}
              </p>
            )}
          </div>

          {t.answer && (
            <div style={{ background: "var(--surface)", borderLeft: "2px solid var(--ink)", padding: "12px 13px" }}>
              <Kicker tone="muted" style={{ marginBottom: 6 }}>What the documents say</Kicker>
              <Markdown>{t.answer}</Markdown>
            </div>
          )}

          {error && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 9, border: "1px solid var(--accent)", padding: "11px 12px" }}>
              <AlertCircle size={15} color="var(--accent)" strokeWidth={2} style={{ flex: "none", marginTop: 1 }} />
              <span style={{ font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--accentDeep)" }}>{error}</span>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Kicker tone="muted">Comments{t.comments && t.comments.length > 0 ? " (" + t.comments.length + ")" : ""}</Kicker>
            <div style={{ display: "flex", gap: 8 }}>
              <textarea
                value={commentText}
                rows={2}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a note…"
                style={{ ...inputStyle, minHeight: 44, resize: "vertical", font: "400 13px/1.4 var(--bq-font-body)", flex: 1 }}
              />
            </div>
            <Button onClick={submitComment} disabled={busy || !commentText.trim()} style={{ alignSelf: "flex-start" }}>
              {busy ? "Adding…" : "Add comment"}
            </Button>

            {(!t.comments || t.comments.length === 0) && (
              <span style={{ font: "400 12.5px/1.4 var(--bq-font-body)", color: "var(--muted)" }}>No comments yet.</span>
            )}
            {t.comments && t.comments.length > 0 && (
              <div style={{ borderTop: "1px solid var(--rule)" }}>
                {t.comments.map((c) => (
                  <div key={c.id} style={{ padding: "11px 2px", borderBottom: "1px solid var(--rule)", display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ font: "700 11px/1.2 var(--bq-font-display)", color: "var(--ink)" }}>
                        {c.actor_role === "board" ? "Community admin" : c.actor_role === "admin" ? "Application admin" : "You"}
                      </span>
                      <span style={{ font: "400 10.5px/1 var(--bq-font-mono)", color: "var(--muted)" }}>
                        {new Date(c.at).toLocaleString()}
                      </span>
                    </div>
                    <span style={{ font: "400 13px/1.5 var(--bq-font-body)", color: "var(--body)", whiteSpace: "pre-wrap" }}>{c.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Board-side editor for the five routing addresses. */
export function ManagerContacts({ managers, onSave }) {
  const [draft, setDraft] = useState(managers);
  const [saved, setSaved] = useState(false);
  const dirty = MANAGER_ROLES.some((m) => (draft[m.id] || "") !== (managers[m.id] || ""));

  React.useEffect(() => { setDraft(managers); }, [managers]);

  const commit = async () => {
    await onSave(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "15px 0", borderBottom: "1px solid var(--rule)" }}>
      <div>
        <div style={{ font: "600 14px/1.2 var(--bq-font-display)" }}>Manager contacts</div>
        <div style={{ font: "400 11.5px/1.35 var(--bq-font-body)", color: "var(--muted)", marginTop: 3 }}>
          Where ResidentIQ routes each kind of request. An unset address queues the ticket instead of emailing it.
        </div>
      </div>
      {MANAGER_ROLES.map((m) => (
        <Field key={m.id} label={m.label}>
          <input
            type="email"
            value={draft[m.id] || ""}
            onChange={(e) => setDraft({ ...draft, [m.id]: e.target.value })}
            placeholder={m.id.replace(/_/g, ".") + "@example.com"}
            style={{ ...inputStyle, font: "400 13px/1.2 var(--bq-font-mono)" }}
          />
        </Field>
      ))}
      <Button onClick={commit} disabled={!dirty} style={{ alignSelf: "flex-start" }}>
        {saved ? "Saved" : "Save contacts"}
      </Button>
    </div>
  );
}
